import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { offerApi } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
const statusColors = {
  PENDING: '#f59e0b', SENT: '#3b82f6', VIEWED: '#8b5cf6',
  SIGNED: '#10b981', REVOKED: '#ef4444', EXPIRED: '#6b7280',
};

const canResend = (status) => ['SENT', 'VIEWED', 'EXPIRED'].includes(status);
const canModify = (status) => ['SENT', 'VIEWED', 'PENDING'].includes(status);
const canPublish = (status) => status === 'PENDING';
const canRevoke = (status) => ['SENT', 'VIEWED'].includes(status);

const s = {
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', marginBottom: 24 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16, marginBottom: 32 },
  card: { background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  statNum: { fontSize: 32, fontWeight: 700 },
  statLabel: { fontSize: 13, color: '#666', marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', background: '#f9fafb', borderBottom: '1px solid #eee' },
  td: { padding: '12px 16px', fontSize: 14, borderBottom: '1px solid #f0f0f0' },
  badge: (status) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: '#fff', background: statusColors[status] || '#999' }),
  actionBtn: (color) => ({ padding: '4px 12px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 4, cursor: 'pointer', background: color, color: '#fff', marginRight: 6 }),
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: 12, padding: 32, width: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, marginBottom: 16, boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, marginBottom: 16, minHeight: 120, resize: 'vertical', fontFamily: 'monospace', boxSizing: 'border-box' },
  btn: { padding: '10px 24px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginRight: 8 },
  cancelBtn: { padding: '10px 24px', background: '#fff', color: '#666', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, cursor: 'pointer' },
  tokenBox: { background: '#1a1a2e', color: '#7c83fd', padding: '10px 16px', borderRadius: 6, fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all', marginTop: 12 },
};

export default function DashboardPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [modifyOffer, setModifyOffer] = useState(null); // offer being modified
  const [modifyForm, setModifyForm] = useState({ title: '', offerDetails: '', expiryHours: 72 });
  const [resultModal, setResultModal] = useState(null); // resend/modify result with new QR

  const { data: stats } = useQuery({ queryKey: ['dashboard'], queryFn: () => offerApi.dashboard().then(r => r.data) });
  const { data: offers } = useQuery({ queryKey: ['offers'], queryFn: () => offerApi.list().then(r => r.data) });

  const resendMutation = useMutation({
    mutationFn: ({ offerId, expiryHours }) => offerApi.resend(offerId, expiryHours),
    onSuccess: (res) => {
      qc.invalidateQueries(['offers']);
      qc.invalidateQueries(['dashboard']);
      setResultModal(res.data);
      toast.success('Offer resent with new QR');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Resend failed'),
  });

  const revokeMutation = useMutation({
    mutationFn: (offerId) => offerApi.revoke(offerId),
    onSuccess: () => {
      qc.invalidateQueries(['offers']);
      qc.invalidateQueries(['dashboard']);
      toast.success('Offer revoked');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Revoke failed'),
  });

  const modifyMutation = useMutation({
    mutationFn: ({ offerId, data }) => offerApi.modify(offerId, data),
    onSuccess: (res) => {
      qc.invalidateQueries(['offers']);
      qc.invalidateQueries(['dashboard']);
      setModifyOffer(null);
      setResultModal(res.data);
      toast.success('Offer modified and resent');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Modify failed'),
  });

  const openModify = (offer) => {
    setModifyOffer(offer);
    setModifyForm({ title: offer.title, offerDetails: '', expiryHours: 72 });
  };

  const submitModify = (e) => {
    e.preventDefault();
    modifyMutation.mutate({
      offerId: modifyOffer.id,
      data: { ...modifyForm, expiryHours: parseInt(modifyForm.expiryHours) },
    });
  };

  const publishMutation = useMutation({
    mutationFn: (offerId) => offerApi.publish(offerId),
    onSuccess: (res) => {
      qc.invalidateQueries(['offers']);
      qc.invalidateQueries(['dashboard']);
      setResultModal(res.data);
      toast.success('Offer published and sent to candidate');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Publish failed'),
  });

  const handleViewPdf = async (offerId) => {
    try {
      const res = await offerApi.pdf(offerId);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('PDF not available — offer may not be signed yet');
    }
  };

  const [viewOffer, setViewOffer] = useState(null);
  const handleViewOffer = async (offerId) => {
    try {
      const res = await offerApi.details(offerId);
      setViewOffer(res.data);
    } catch {
      toast.error('Failed to load offer details');
    }
  };

  const joinedMutation = useMutation({
    mutationFn: (offerId) => offerApi.markJoined(offerId),
    onSuccess: () => { qc.invalidateQueries(['offers']); qc.invalidateQueries(['dashboard']); toast.success('Candidate marked as joined. Offer letter unlocked and email sent.'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed'),
  });

  const noShowMutation = useMutation({
    mutationFn: (offerId) => offerApi.markNoShow(offerId),
    onSuccess: () => { qc.invalidateQueries(['offers']); qc.invalidateQueries(['dashboard']); toast.success('Marked as no-show'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed'),
  });

  return (
    <div>
      <h1 style={s.title}>Dashboard</h1>

      {stats && (
        <div style={s.grid}>
          {Object.entries(stats).map(([status, count]) => (
            <div key={status} style={s.card}>
              <div style={{ ...s.statNum, color: statusColors[status] }}>{count}</div>
              <div style={s.statLabel}>{status}</div>
            </div>
          ))}
        </div>
      )}

      <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#1a1a2e' }}>Recent Offers</h2>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Candidate</th>
            <th style={s.th}>Title</th>
            <th style={s.th}>Status</th>
            <th style={s.th}>Expiry</th>
            <th style={s.th}>Created</th>
            <th style={s.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {offers?.content?.map(offer => (
            <tr key={offer.id}>
              <td style={s.td}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{offer.candidateName || '—'}</div>
                {offer.candidateDisplayId && <div style={{ fontSize: 11, color: '#7c83fd', fontFamily: 'monospace' }}>{offer.candidateDisplayId}</div>}
              </td>
              <td style={s.td}>{offer.title}</td>
              <td style={s.td}><span style={s.badge(offer.status)}>{offer.status}</span></td>
              <td style={s.td}>{format(new Date(offer.expiry), 'MMM d, yyyy HH:mm')}</td>
              <td style={s.td}>{format(new Date(offer.createdAt), 'MMM d, yyyy')}</td>
              <td style={s.td}>
                {['SENT', 'VIEWED', 'PENDING'].includes(offer.status) && (
                  <button style={s.actionBtn('#8b5cf6')} onClick={() => handleViewOffer(offer.id)}>
                    👁 View
                  </button>
                )}
                {canPublish(offer.status) && (
                  <button style={s.actionBtn('#10b981')}
                    onClick={() => { if (window.confirm('Publish this offer? It will be sent to the candidate.')) publishMutation.mutate(offer.id); }}
                    disabled={publishMutation.isPending}>
                    📤 Publish
                  </button>
                )}
                {canModify(offer.status) && (
                  <button style={s.actionBtn('#7c83fd')} onClick={() => openModify(offer)}>
                    ✏️ Modify
                  </button>
                )}
                {canResend(offer.status) && (
                  <button style={s.actionBtn('#3b82f6')}
                    onClick={() => resendMutation.mutate({ offerId: offer.id, expiryHours: 72 })}
                    disabled={resendMutation.isPending}>
                    📧 Resend Email
                  </button>
                )}
                {canRevoke(offer.status) && (
                  <button style={s.actionBtn('#ef4444')}
                    onClick={() => { if (window.confirm('Revoke this offer? The candidate will no longer be able to sign it.')) revokeMutation.mutate(offer.id); }}
                    disabled={revokeMutation.isPending}>
                    🚫 Revoke
                  </button>
                )}
                {offer.status === 'SIGNED' && (
                  <>
                    <button style={s.actionBtn('#10b981')} onClick={() => handleViewPdf(offer.id)}>
                      📄 View PDF
                    </button>
                    {offer.joiningStatus === 'PENDING_JOIN' && (
                      <>
                        <button style={s.actionBtn('#059669')}
                          onClick={() => { if (window.confirm('Mark this candidate as joined? This will unlock their offer letter.')) joinedMutation.mutate(offer.id); }}
                          disabled={joinedMutation.isPending}>
                          ✅ Joined
                        </button>
                        <button style={s.actionBtn('#ef4444')}
                          onClick={() => { if (window.confirm('Mark as no-show?')) noShowMutation.mutate(offer.id); }}
                          disabled={noShowMutation.isPending}>
                          ❌ No Show
                        </button>
                      </>
                    )}
                    {offer.joiningStatus === 'JOINED' && (
                      <span style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>🎉 Joined</span>
                    )}
                    {offer.joiningStatus === 'NO_SHOW' && (
                      <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>❌ No Show</span>
                    )}
                    <button style={s.actionBtn('#f59e0b')}
                      onClick={() => navigate(`/offers/create?candidateId=${offer.candidateId}&candidateName=${encodeURIComponent(offer.candidateName || '')}`)}>
                      ✏️ New Offer
                    </button>
                  </>
                )}              </td>
            </tr>
          ))}
          {!offers?.content?.length && (
            <tr><td style={{ ...s.td, color: '#999', textAlign: 'center' }} colSpan={5}>No offers yet</td></tr>
          )}
        </tbody>
      </table>

      {/* Modify Modal */}
      {modifyOffer && (
        <div style={s.overlay} onClick={() => setModifyOffer(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontWeight: 700, marginBottom: 20 }}>✏️ Modify Offer</h3>
            <form onSubmit={submitModify}>
              <label style={s.label}>Offer Title</label>
              <input style={s.input} value={modifyForm.title}
                onChange={e => setModifyForm({ ...modifyForm, title: e.target.value })} />

              <label style={s.label}>Offer Details (JSON) — leave blank to keep existing</label>
              <textarea style={s.textarea} placeholder='{ "salary": "130,000", ... }'
                value={modifyForm.offerDetails}
                onChange={e => setModifyForm({ ...modifyForm, offerDetails: e.target.value })} />

              <label style={s.label}>New Expiry (hours)</label>
              <input style={s.input} type="number" min={1} max={720} value={modifyForm.expiryHours}
                onChange={e => setModifyForm({ ...modifyForm, expiryHours: e.target.value })} />

              <div>
                <button style={s.btn} type="submit" disabled={modifyMutation.isPending}>
                  {modifyMutation.isPending ? 'Saving...' : 'Save & Resend'}
                </button>
                <button style={s.cancelBtn} type="button" onClick={() => setModifyOffer(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Offer Modal */}
      {viewOffer && (
        <div style={s.overlay} onClick={() => setViewOffer(null)}>
          <div style={{ ...s.modal, maxWidth: 800, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: '#1a1a2e', padding: '20px 32px', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 0 }}>
              {viewOffer.logoBase64 ? (
                <img src={`data:${viewOffer.logoContentType || 'image/png'};base64,${viewOffer.logoBase64}`} alt="logo" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'contain', background: '#fff', padding: 3 }} />
              ) : (
                <div style={{ width: 44, height: 44, borderRadius: 8, background: '#7c83fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🏢</div>
              )}
              <div>
                <div style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>{viewOffer.watermark?.tenantName || 'SecureOffer'}</div>
                <div style={{ color: '#aaa', fontSize: 11 }}>Official Offer Letter</div>
              </div>
            </div>
            {(() => {
              const wm = viewOffer.watermark || {};
              const candidateName = wm.candidateName || '';
              const tenantName = wm.tenantName || 'SecureOffer';
              const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
              let fields = {};
              try { fields = JSON.parse(viewOffer.offerDetails || '{}'); } catch (_) {}
              const position = fields.position || viewOffer.title;
              const salary = fields.salary || 'as discussed';
              const startDate = fields.startDate || 'as agreed';
              const location = fields.location || 'our office';
              const empType = fields.employmentType || 'Full-time';
              const department = fields.department || '';
              const reportingTo = fields.reportingTo || '';
              const labelMap = { position: 'Position', employmentType: 'Employment Type', salary: 'Annual Compensation', startDate: 'Start Date', location: 'Location', department: 'Department', reportingTo: 'Reporting To', probation: 'Probation Period', benefits: 'Benefits' };
              return (
                <div style={{ padding: '32px 40px', fontFamily: 'Georgia, serif', color: '#222', lineHeight: 1.8 }}>
                  <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>Date: {today}</p>
                  <p style={{ marginBottom: 4 }}>To,</p>
                  <p style={{ fontWeight: 700, marginBottom: 20 }}>{candidateName}</p>
                  <p style={{ fontWeight: 700, fontSize: 15, borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 16 }}>Subject: Offer of Employment — {viewOffer.title}</p>
                  <p style={{ marginBottom: 12 }}>Dear {candidateName},</p>
                  <p style={{ marginBottom: 12 }}>We are delighted to extend this offer of employment to you for the position of <strong>{position}</strong> at <strong>{tenantName}</strong>.</p>
                  <p style={{ fontWeight: 700, color: '#7c83fd', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, marginTop: 20 }}>Terms of Employment</p>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20, fontSize: 13 }}>
                    <tbody>
                      {Object.entries(fields).map(([k, v]) => (
                        <tr key={k} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '7px 12px', background: '#f8f9fa', fontWeight: 600, width: '35%', color: '#555' }}>{labelMap[k] || k}</td>
                          <td style={{ padding: '7px 12px', color: '#1a1a2e' }}>{String(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p style={{ marginBottom: 12 }}>Your annual compensation package will be <strong>₹{salary}</strong>. {empType && <>This is a <strong>{empType}</strong> position.</>} {department && <>You will be part of the <strong>{department}</strong> department.</>} {reportingTo && <>You will report to <strong>{reportingTo}</strong>.</>}</p>
                  <p style={{ marginBottom: 20 }}>Warm regards,</p>
                  <p style={{ fontWeight: 700, marginBottom: 2 }}>{tenantName}</p>
                  <p style={{ color: '#888', fontSize: 13 }}>Human Resources Department</p>
                </div>
              );
            })()}
            <div style={{ padding: '16px 40px', borderTop: '1px solid #eee', textAlign: 'right' }}>
              <button style={s.btn} onClick={() => setViewOffer(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Result Modal — shows new QR after resend/modify */}
      {resultModal && (
        <div style={s.overlay} onClick={() => setResultModal(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontWeight: 700, color: '#166534', marginBottom: 16 }}>✅ New Offer Link Generated</h3>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
              <QRCodeSVG value={resultModal.qrUrl} size={180} level="H" />
            </div>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>Share this link with the candidate:</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <a href={resultModal.qrUrl} target="_blank" rel="noopener noreferrer"
                style={{ ...s.tokenBox, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none', display: 'block' }}>
                {resultModal.qrUrl}
              </a>
            </div>
            {/* Token only — easy to copy for mobile app */}
            <p style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Token only (paste in mobile app):</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ ...s.tokenBox, flex: 1, fontSize: 10, wordBreak: 'break-all' }}>
                {resultModal.qrUrl?.split('/offer/view/')[1]}
              </div>
              <button
                onClick={() => {
                  const token = resultModal.qrUrl?.split('/offer/view/')[1];
                  navigator.clipboard.writeText(token);
                  alert('Token copied! Now paste it in the mobile app.');
                }}
                style={{ padding: '8px 14px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                📋 Copy Token
              </button>
            </div>
            <p style={{ fontSize: 12, color: '#888', marginTop: 12 }}>
              Expires: {new Date(resultModal.expiry).toLocaleString()}
            </p>
            <button style={{ ...s.btn, marginTop: 16 }} onClick={() => setResultModal(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
